import React, { useEffect, useState } from "react";
import FooterNew from "../components/Footer-New";
import NavbarNew from "../components/Navbar-New.js";


export default function RetirementPlanning() {
  const [activeSection, setActiveSection] = useState("new-reality");

  const sections = [
    { id: "new-reality", title: "A New Reality for Retirement Planning" },
    { id: "interest-rates", title: "Why Interest Rates Matter So Much" },
    { id: "rigid-plans", title: "The Problem With Rigid Plans" },
    { id: "flexible-income", title: "Building Flexible Income Streams" },
    { id: "higher-rates", title: "Taking Advantage of Higher Rates" },
    { id: "inflation", title: "Don’t Forget About Inflation" },
    { id: "emotional-side", title: "The Emotional Side of Money Decisions" },
    { id: "moving-forward", title: "Moving Forward With Confidence" },
  ];

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#f4f3f3] min-h-screen">
     
      <NavbarNew />
      
      <div className="w-full px-4 sm:px-6 md:px-8  py-8 sm:py-12   2xl:py-[80px]">
        <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row justify-between items-start gap-12 2xl:gap-16">
          
          {/* Left Content Area */}
          <div className="w-full lg:w-2/3 xl:w-3/4 flex flex-col gap-8 bg-white p-6 md:p-10 lg:p-14 rounded-lg shadow-sm border border-[#395645]/10">
            
            {/* Main Title */}
            <h1 className="text-[#395645] text-3xl sm:text-4xl lg:text-5xl xl:text-[56px]  font-normal font-['Forum'] ">
              Planning for Retirement in a Changing 
              Rate Environment
            </h1>

            {/* Hero Image */}
            <div className="w-full  overflow-hidden rounded-sm md:rounded-lg">
              <img 
                src={require("../assets/images/home/service_02.png")} 
                alt="Retirement Planning" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Body Content */}
            <div className="text-[#3a4f66] text-base md:text-lg font-normal font-['Roobert'] leading-relaxed flex flex-col gap-6">
              <p>
                Retirement planning has never been completely straightforward but these days, it’s definitely a bit more unpredictable. Interest rates don’t move the way they used to. They can jump quickly, drop unexpectedly, and generally behave in ways that make long-term planning trickier.
              </p>
              <p>
                If you’re in your 50s or beyond, this doesn’t mean you need to start from scratch. It just means adjusting your strategy to fit a more flexible, fast changing environment.
              </p>

              {/* Sections */}
              <div id="new-reality" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-4">A New Reality for Retirement Planning</h3>
                <p className="mb-4">Not too long ago, retirement planning felt more stable. Savings accounts offered steady (if modest) returns, bond yields were fairly predictable, and long-term projections felt a bit more reliable.</p>
                <p className="mb-4">Now, things have shifted. Central banks change rates more aggressively, inflation can spike without much warning, and markets react almost instantly.</p>
                <p>That can feel unsettling especially as retirement gets closer. But uncertainty doesn’t automatically mean risk. With the right approach, it just means building in a bit more flexibility.</p>
              </div>

              <div id="interest-rates" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Why Interest Rates Matter So Much</h3>
                <p className="mb-4">Interest rates affect a lot more than just your savings account, they influence almost everything in your financial plan.</p>
                <p className="mb-4">When rates go up, safer options like savings and certain bonds start looking more appealing. You can often earn more without taking on much risk. On the flip side, borrowing gets more expensive, and stock markets can feel the pressure.</p>
                <p className="mb-4">When rates drop, it’s usually the opposite. Borrowing is cheaper, stocks may benefit, but income from safer investments can shrink.</p>
                <p className="mb-4">This creates a common challenge: where should your income come from, and how do you balance stability with growth?</p>
                <p className="font-semibold text-[#395645]">The goal isn’t to predict what’s next, it’s to avoid relying too heavily on any one scenario.</p>
              </div>

              <div id="rigid-plans" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">The Problem With Rigid Plans</h3>
                <p className="mb-4">One of the biggest mistakes right now is sticking too tightly to a single strategy.</p>
                <p className="mb-4">A plan that worked well during low interest rates might not perform the same way today. And going all-in on high-yield opportunities now could backfire if rates fall again.</p>
                <p className="mb-4">Being too rigid can limit your options. Holding too much cash might feel safe, but it can slow down growth. Staying heavily invested in volatile markets without adjusting can expose you to unnecessary ups and downs especially when stability becomes more important.</p>
                <p>These days, a good retirement plan isn’t about being perfect—it’s about being adaptable.</p>
              </div>

              <div id="flexible-income" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Building Flexible Income Streams</h3>
                <p className="mb-4">Instead of relying on just one income source, it helps to have a mix.</p>
                <p className="mb-4">Some investments can provide steady, predictable income, while others are there to grow over time and help keep up with inflation. This balance makes it easier to adjust as conditions change, without needing to constantly overhaul your plan.</p>
                <p className="mb-4">It also takes the pressure off trying to time the market or guess where interest rates are heading something even professionals struggle with.</p>
                <p>Flexibility also means being open to adjusting how and when you draw income. For example, taking a bit less during tougher market periods and more when things are stronger can help your savings last longer.</p>
              </div>

              <div id="higher-rates" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Taking Advantage of Higher Rates (Without Going All-In)</h3>
                <p className="mb-4">Higher interest rates can feel like a great opportunity and in many ways, they are.</p>
                <p className="mb-4">You might earn more on savings, find better returns from fixed-income investments, or access income products with improved payouts.</p>
                <p className="mb-4">But it’s important not to go all-in on the current moment. Locking everything into long-term options could limit your flexibility if rates shift again.</p>
                <p>A balanced approach works best take advantage of what’s available now, but keep some options open for later.</p>
              </div>

              <div id="inflation" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Don’t Forget About Inflation</h3>
                <p className="mb-4">Even when interest rates are higher, inflation is still a big factor to keep in mind.</p>
                <p className="mb-4">Retirement can last decades, and over time, the cost of living can change a lot. Without some level of growth in your portfolio, your purchasing power can slowly decline.</p>
                <p className="mb-4">That’s why growth still matters even as you move toward safer investments.</p>
                <p>The key is balance: enough growth to keep up with inflation, and enough stability to give you peace of mind.</p>
              </div>

              <div id="emotional-side" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">The Emotional Side of Money Decisions</h3>
                <p className="mb-4">It’s not just about numbers there’s a human side to all of this too.</p>
                <p className="mb-4">When markets shift quickly, it’s easy to feel uncertain. That can lead to reactive decisions, like moving everything into “safe” assets during downturns or chasing higher returns when opportunities pop up.</p>
                <p className="mb-4">But making frequent, emotion-driven changes can hurt your long-term results.</p>
                <p>A solid plan helps you stay focused, so you’re not constantly reacting to short-term noise.</p>
              </div>

              <div id="moving-forward" className="scroll-mt-28">
                <h3 className="text-[#395645] text-2xl md:text-3xl font-['Forum'] mb-3 mt-6">Moving Forward With Confidence</h3>
                <p className="mb-4">Retirement planning today isn’t about guessing where interest rates are headed next. It’s about building a plan that can handle different outcomes.</p>
                <p className="mb-4">If you’re already in your 50s or beyond, you likely have a solid foundation. Now it’s about fine-tuning adding flexibility, balancing your income sources, and staying aware of both risks and opportunities.</p>
                <p>Rates will change. Markets will evolve. But with a thoughtful, adaptable approach, your retirement plan can stay on track and continue supporting the lifestyle you’ve worked hard for.</p>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Table of Contents */}
          <div className="w-full lg:w-1/3 xl:w-1/4 hidden lg:block sticky top-[120px]">
                      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm border border-[#395645]/10">

              <div className="flex items-center gap-2 mb-6 border-b border-[#395645]/10 pb-4">
                {/* <div className="w-2 h-2 bg-[#395645] rounded-full" /> */}
                                <h3 className="text-[#395645] text-xl sm:text-2xl font-['Forum']">Table of Contents</h3>

              </div>
              <ul className="flex flex-col gap-3">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a 
                      href={`#${s.id}`} 
                      onClick={(e) => scrollToSection(e, s.id)}
                      className={`block py-1 text-sm sm:text-[15px] font-['Roobert'] transition-all duration-300 border-l-2 pl-4 ${

                        activeSection === s.id 
                        ? 'border-[#395645] text-[#395645] font-semibold bg-[#395645]/5' 
                        : 'border-transparent text-[#3a4f66]/70 hover:text-[#395645] hover:border-[#395645]/30'
                      }`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>

      <FooterNew />
    </div>
  );
}
